import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ArrowLeft, Clock, Lightbulb, Target } from 'lucide-react';
import RelatedContent from '../components/RelatedContent';
import SEO, { buildLessonJsonLd, buildBreadcrumbJsonLd } from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LessonDetail = () => {
  const { slug } = useParams();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const subEmail = localStorage.getItem('subscriber_email') || '';
        const headers = subEmail ? { 'X-Subscriber-Email': subEmail } : {};
        const res = await axios.get(`${API_URL}/api/lessons/${slug}`, { headers });
        setLesson(res.data);
        window.scrollTo(0, 0);
      } catch (e) {
        if (e.response?.status === 404) setError('not_found');
        else setError('Failed to load lesson');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="lesson-loading">
        <Loader2 className="w-8 h-8 animate-spin text-gray-600" />
      </div>
    );
  }

  if (error === 'not_found' || !lesson) {
    return (
      <div className="min-h-screen pt-32 px-6" data-testid="lesson-not-found">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl font-light mb-4">Lesson Not Found</h1>
          <Link to="/learn" className="inline-flex items-center gap-2 text-gray-600 hover:text-black">
            <ArrowLeft size={20} /> Back to Learn
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="lesson-detail-page">
      <SEO
        title={lesson.title}
        description={lesson.summary}
        path={`/learn/${lesson.slug}`}
        type="article"
        publishedAt={lesson.created_at || lesson.date}
        updatedAt={lesson.updated_at || lesson.created_at || lesson.date}
        keywords={[lesson.topic, 'AI lesson', 'learn AI', 'AI for students', 'AI ethics']}
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            buildLessonJsonLd({
              title: lesson.title,
              description: lesson.summary,
              slug: lesson.slug,
              topic: lesson.topic,
              publishedAt: lesson.created_at || lesson.date,
              faqs: lesson.analogy && lesson.tryThis
                ? [
                    { question: `What is ${lesson.title}?`, answer: lesson.summary },
                    { question: `How can I think about ${lesson.topic}?`, answer: lesson.analogy },
                    { question: `How can I try ${lesson.topic} myself?`, answer: lesson.tryThis },
                  ]
                : [],
            }),
            buildBreadcrumbJsonLd([
              { name: 'Home', url: '/' },
              { name: 'Learn', url: '/learn' },
              { name: lesson.topic || 'Lesson', url: '/learn' },
              { name: lesson.title, url: `/learn/${lesson.slug}` },
            ]),
          ],
        }}
      />
      <div className="pt-32 pb-8 px-6">
        <div className="max-w-4xl mx-auto">
          <Link to="/learn" className="inline-flex items-center gap-2 text-gray-600 hover:text-black transition-colors">
            <ArrowLeft size={20} /> Back to Learn
          </Link>
        </div>
      </div>

      <article className="pb-24 px-6">
        <div className="max-w-4xl mx-auto">
          <span className="inline-block text-sm font-medium text-gray-600 mb-4">{lesson.topic}</span>
          <h1 className="text-4xl md:text-5xl font-light mb-6 leading-tight" data-testid="lesson-title">
            {lesson.title}
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed mb-8">{lesson.summary}</p>

          <div className="flex items-center gap-2 text-sm text-gray-500 mb-12">
            <Clock size={16} />
            <span>{lesson.readTime}</span>
            <span className="mx-2">·</span>
            <span>{new Date(lesson.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
          </div>

          {/* TL;DR — quotable opener */}
          {lesson.tldr ? (
            <aside
              className="mb-10 p-6 rounded-xl border-l-4 border-indigo-500 bg-indigo-50/50"
              data-testid="lesson-tldr"
            >
              <h2 className="text-xs font-semibold uppercase tracking-wider text-indigo-700 mb-2">TL;DR</h2>
              <p className="text-gray-800 leading-relaxed text-base">{lesson.tldr}</p>
            </aside>
          ) : null}

          <div className="prose prose-lg max-w-none mb-12" data-testid="lesson-content">
            {(lesson.content || '').split('\n').map((line, i) => {
              if (line.startsWith('## ')) return <h2 key={i} className="text-2xl font-medium mt-10 mb-4">{line.replace('## ', '')}</h2>;
              if (line.startsWith('### ')) return <h3 key={i} className="text-xl font-medium mt-8 mb-3">{line.replace('### ', '')}</h3>;
              if (line.startsWith('- ')) return <li key={i} className="ml-6 mb-2 text-gray-700">{line.replace('- ', '')}</li>;
              if (line.trim() === '') return null;
              return <p key={i} className="mb-5 text-gray-700 leading-relaxed">{line}</p>;
            })}
          </div>

          {lesson.analogy && (
            <div className="mb-8 bg-amber-50 border-l-4 border-amber-400 rounded-r-lg p-6" data-testid="lesson-analogy">
              <div className="flex items-start gap-3">
                <Lightbulb className="text-amber-600 flex-shrink-0 mt-1" size={20} />
                <div>
                  <h4 className="text-sm font-semibold text-amber-900 mb-1">Think of it like this</h4>
                  <p className="text-gray-800 leading-relaxed">{lesson.analogy}</p>
                </div>
              </div>
            </div>
          )}

          {lesson.tryThis && (
            <div className="mb-8 bg-emerald-50 border-l-4 border-emerald-400 rounded-r-lg p-6" data-testid="lesson-try-this">
              <div className="flex items-start gap-3">
                <Target className="text-emerald-600 flex-shrink-0 mt-1" size={20} />
                <div>
                  <h4 className="text-sm font-semibold text-emerald-900 mb-1">Try this</h4>
                  <p className="text-gray-800 leading-relaxed">{lesson.tryThis}</p>
                </div>
              </div>
            </div>
          )}

          {/* Related Posts (cross-link for SEO) */}
          <RelatedContent kind="lesson" slug={lesson.slug} />
        </div>
      </article>
    </div>
  );
};

export default LessonDetail;
